import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, CheckCircle2, Clock, Eye, Pencil, GitCompare, Upload,
  History, RotateCcw, X, AlertTriangle, Loader2, ChevronDown, ChevronUp,
  Save, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── tiny helpers ─────────────────────────────────────────── */
const fmtDt = iso => iso ? new Date(iso).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—';

const StatusBadge = ({ status }) =>
  status === 'published'
    ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold" style={{ background: 'rgba(52,211,153,0.1)', color: '#34d399', border: '1px solid rgba(52,211,153,0.2)' }}>
        <CheckCircle2 size={10} /> Published
      </span>
    : <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold" style={{ background: 'rgba(251,191,36,0.1)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.2)' }}>
        <Clock size={10} /> Stored — Not Published
      </span>;

const BioText = ({ content, testId }) => {
  const paras = (content || '').split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  return (
    <div className="space-y-4" data-testid={testId}>
      {paras.map((p, i) => (
        <p key={i} className="text-sm leading-relaxed" style={{ color: 'rgba(212,204,188,0.7)', fontFamily: 'Georgia, serif' }}>{p}</p>
      ))}
    </div>
  );
};

/* ─── Confirmation Dialog ──────────────────────────────────── */
const PublishConfirmDialog = ({ onConfirm, onCancel, publishing }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}
    onClick={onCancel}>
    <div className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
      style={{ background: '#071B3A', border: '1px solid rgba(251,191,36,0.2)' }}
      onClick={e => e.stopPropagation()}>
      <div className="px-8 pt-8 pb-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.2)' }}>
            <AlertTriangle size={18} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-cream/90 font-semibold text-base mb-1">Publish Future Version</h3>
            <p className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(251,191,36,0.6)' }}>Irreversible action — requires explicit confirmation</p>
          </div>
        </div>

        <div className="p-5 rounded-xl mb-6" style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.12)' }}>
          <p className="text-sm leading-relaxed text-cream/80">
            You are about to replace the current About page with the approved future version describing Dr Promise Monday as <strong className="text-amber-300">Nigerian-British</strong>.
            Confirm that the British citizenship ceremony has been completed before publishing.
          </p>
        </div>

        <p className="text-xs text-cream/40 mb-6">
          The current version will be preserved in version history. This action cannot be undone automatically — you can rollback from the Version History section if needed.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            data-testid="publish-confirm-cancel"
            className="flex-1 py-3 rounded-xl text-sm font-medium transition-all"
            style={{ background: 'rgba(212,204,188,0.06)', color: 'rgba(212,204,188,0.5)', border: '1px solid rgba(212,204,188,0.08)' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={publishing}
            data-testid="publish-confirm-submit"
            className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: '#C99316', color: '#071B3A' }}
          >
            {publishing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {publishing ? 'Publishing…' : 'Confirm & Publish'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Edit modal ────────────────────────────────────────────── */
const EditModal = ({ versionType, content, onSave, onClose }) => {
  const [text, setText] = useState(content || '');
  const [saving, setSaving] = useState(false);
  const label = versionType === 'current' ? 'Current Live About' : 'Future Approved About';

  const handleSave = async () => {
    setSaving(true);
    await onSave(text);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={onClose}>
      <div className="w-full rounded-2xl shadow-2xl flex flex-col"
        style={{ maxWidth: '780px', maxHeight: '92vh', background: '#071B3A', border: '1px solid rgba(201,147,22,0.12)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(201,147,22,0.07)' }}>
          <div>
            <h3 className="text-cream/90 font-semibold text-sm">Edit — {label}</h3>
            <p className="text-[10px] text-cream/30 mt-0.5">Each blank line between paragraphs creates a new paragraph. Do not alter wording without explicit approval.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-cream/30 hover:text-cream/60 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 p-6 overflow-hidden flex flex-col">
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            data-testid="about-edit-textarea"
            className="flex-1 w-full rounded-xl px-5 py-4 text-sm leading-relaxed resize-none focus:outline-none"
            style={{
              background: 'rgba(7,15,32,0.8)', border: '1px solid rgba(201,147,22,0.1)',
              color: 'rgba(212,204,188,0.8)', fontFamily: 'Georgia, serif', minHeight: '400px',
            }}
            placeholder="Enter biography text. Separate paragraphs with a blank line."
          />
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: 'rgba(201,147,22,0.07)' }}>
          <p className="text-[11px] text-cream/25">{text.split(/\n\n+/).filter(Boolean).length} paragraphs</p>
          <div className="flex gap-3">
            <button onClick={onClose}
              className="px-5 py-2 rounded-lg text-xs text-cream/40 transition-all"
              style={{ border: '1px solid rgba(212,204,188,0.08)' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !text.trim()}
              data-testid="about-edit-save"
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
              style={{ background: '#C99316', color: '#071B3A' }}>
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Compare view ──────────────────────────────────────────── */
const CompareView = ({ current, future, onClose }) => {
  const curParas = (current || '').split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  const futParas = (future || '').split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  const maxLen = Math.max(curParas.length, futParas.length);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.78)' }}
      onClick={onClose}>
      <div className="w-full rounded-2xl shadow-2xl flex flex-col"
        style={{ maxWidth: '1100px', maxHeight: '94vh', background: '#071B3A', border: '1px solid rgba(201,147,22,0.1)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(201,147,22,0.07)' }}>
          <h3 className="text-cream/80 font-semibold text-sm flex items-center gap-2">
            <GitCompare size={15} className="text-gold" /> Compare Versions
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg text-cream/30 hover:text-cream/60 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-0 flex-1 overflow-hidden" style={{ borderBottom: '1px solid rgba(201,147,22,0.07)' }}>
          <div className="p-6 overflow-y-auto border-r" style={{ borderColor: 'rgba(201,147,22,0.07)' }}>
            <div className="flex items-center gap-2 mb-5">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-cream/40">Current Live</span>
              <StatusBadge status="published" />
            </div>
            <div className="space-y-4">
              {curParas.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed"
                  style={{
                    color: p === futParas[i] ? 'rgba(212,204,188,0.55)' : 'rgba(212,204,188,0.85)',
                    fontFamily: 'Georgia, serif',
                    background: p === futParas[i] ? 'transparent' : 'rgba(239,68,68,0.04)',
                    padding: p === futParas[i] ? '0' : '4px 0',
                  }}>
                  {p}
                </p>
              ))}
            </div>
          </div>
          <div className="p-6 overflow-y-auto">
            <div className="flex items-center gap-2 mb-5">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-cream/40">Future Approved</span>
              <StatusBadge status="stored" />
            </div>
            <div className="space-y-4">
              {Array.from({ length: maxLen }, (_, i) => {
                const p = futParas[i];
                const changed = p !== curParas[i];
                return p ? (
                  <p key={i} className="text-sm leading-relaxed"
                    style={{
                      color: changed ? 'rgba(212,204,188,0.9)' : 'rgba(212,204,188,0.55)',
                      fontFamily: 'Georgia, serif',
                      background: changed ? 'rgba(52,211,153,0.05)' : 'transparent',
                      padding: changed ? '4px 0' : '0',
                    }}>
                    {p}
                  </p>
                ) : null;
              })}
            </div>
          </div>
        </div>
        <div className="px-6 py-3 flex items-center gap-6 text-[11px]" style={{ color: 'rgba(212,204,188,0.3)' }}>
          <span className="flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 2, background: 'rgba(239,68,68,0.15)', display: 'inline-block' }} /> Changed in Current</span>
          <span className="flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 2, background: 'rgba(52,211,153,0.12)', display: 'inline-block' }} /> Changed in Future</span>
          <span className="flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 2, background: 'rgba(212,204,188,0.06)', display: 'inline-block' }} /> Unchanged</span>
        </div>
      </div>
    </div>
  );
};

/* ─── Preview modal ─────────────────────────────────────────── */
const PreviewModal = ({ content, label, onClose }) => {
  const paras = (content || '').split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={onClose}>
      <div className="w-full rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ maxWidth: '680px', maxHeight: '92vh', background: '#FAF8F3', border: '1px solid rgba(7,27,58,0.1)' }}
        onClick={e => e.stopPropagation()}>
        <div className="px-8 py-5 flex items-center justify-between" style={{ background: '#071B3A' }}>
          <div>
            <h3 className="text-white/90 font-semibold text-sm">Preview — {label}</h3>
            <p className="text-white/40 text-xs mt-0.5">As it will appear on the public About page</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-white/40 hover:text-white/70 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-8 py-8">
          <span className="block text-[11px] tracking-[0.25em] uppercase font-sans font-semibold mb-8" style={{ color: '#C99316' }}>Biography</span>
          <div className="space-y-6">
            {paras.map((p, i) => (
              <p key={i} className="font-serif font-light leading-[1.95] tracking-wide text-base" style={{ color: 'rgba(7,27,58,0.72)' }}>{p}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════════ */
const AdminAbout = () => {
  const { token } = useAuth();
  const hdrs = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const [current, setCurrent]     = useState(null);
  const [future, setFuture]       = useState(null);
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const [editTarget, setEditTarget]       = useState(null); // 'current' | 'future'
  const [showPreview, setShowPreview]     = useState(null); // 'current' | 'future'
  const [showCompare, setShowCompare]     = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [publishing, setPublishing]       = useState(false);
  const [rollingBack, setRollingBack]     = useState(null);

  const loadVersions = async () => {
    try {
      const r = await fetch(`${API_URL}/api/cms/about/versions`, { headers: hdrs() });
      if (r.ok) {
        const d = await r.json();
        setCurrent(d.current);
        setFuture(d.future);
      }
    } catch { toast.error('Failed to load about versions'); }
    setLoading(false);
  };

  const loadHistory = async () => {
    try {
      const r = await fetch(`${API_URL}/api/cms/about/history`, { headers: hdrs() });
      if (r.ok) setHistory((await r.json()).history || []);
    } catch {}
  };

  useEffect(() => { loadVersions(); }, []); // eslint-disable-line
  useEffect(() => { if (showHistory) loadHistory(); }, [showHistory]); // eslint-disable-line

  const handleSaveEdit = async (text) => {
    try {
      const url = editTarget === 'current' ? '/api/cms/about/current' : '/api/cms/about/future';
      const r = await fetch(`${API_URL}${url}`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ content: text }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Save failed');
      toast.success(`${editTarget === 'current' ? 'Current' : 'Future'} version saved`);
      setEditTarget(null);
      loadVersions();
    } catch (e) { toast.error(e.message); }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const r = await fetch(`${API_URL}/api/cms/about/publish-future`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ confirmation: 'CONFIRM_PUBLISH_FUTURE_ABOUT' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Publish failed');
      toast.success('Future version is now live on the About page');
      setShowPublishConfirm(false);
      loadVersions();
      if (showHistory) loadHistory();
    } catch (e) { toast.error(e.message); }
    setPublishing(false);
  };

  const handleRollback = async (historyId) => {
    setRollingBack(historyId);
    try {
      const r = await fetch(`${API_URL}/api/cms/about/rollback/${historyId}`, {
        method: 'POST', headers: hdrs(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Rollback failed');
      toast.success('Rolled back successfully');
      loadVersions();
      loadHistory();
    } catch (e) { toast.error(e.message); }
    setRollingBack(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={20} className="text-gold/40 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-about-page">

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gold/[0.1] border border-gold/[0.1] flex items-center justify-center">
            <FileText size={15} className="text-gold" />
          </div>
          <div>
            <h1 className="text-cream/90 font-semibold text-lg">About Page Content</h1>
            <p className="text-cream/30 text-xs">Manage Current and Future biography versions</p>
          </div>
        </div>
        <button
          onClick={() => setShowCompare(true)}
          data-testid="about-compare-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all"
          style={{ background: 'rgba(201,147,22,0.08)', border: '1px solid rgba(201,147,22,0.18)', color: 'rgba(201,147,22,0.75)' }}
        >
          <GitCompare size={13} /> Compare Versions
        </button>
      </div>

      {/* Two-column layout */}
      <div className="grid lg:grid-cols-2 gap-5">

        {/* ── CURRENT LIVE VERSION ─────────────────────────────── */}
        <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(7,27,58,0.6)', border: '1px solid rgba(201,147,22,0.08)' }}
          data-testid="about-current-panel">
          <div className="px-6 py-5 border-b flex items-start justify-between gap-4" style={{ borderColor: 'rgba(201,147,22,0.06)' }}>
            <div>
              <div className="flex items-center gap-2.5 mb-2">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-cream/40">Version 1</span>
                <StatusBadge status="published" />
              </div>
              <h2 className="text-cream/85 font-semibold text-base">Current Live About</h2>
              {current?.published_at && (
                <p className="text-[11px] text-cream/25 mt-1">Published {fmtDt(current.published_at)}</p>
              )}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => setShowPreview('current')}
                data-testid="about-preview-current"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] transition-all"
                style={{ color: 'rgba(212,204,188,0.45)', border: '1px solid rgba(212,204,188,0.08)' }}
              >
                <Eye size={11} /> Preview
              </button>
              <button
                onClick={() => setEditTarget('current')}
                data-testid="about-edit-current"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] transition-all"
                style={{ color: 'rgba(201,147,22,0.65)', border: '1px solid rgba(201,147,22,0.15)' }}
              >
                <Pencil size={11} /> Edit
              </button>
            </div>
          </div>
          <div className="p-6 max-h-80 overflow-y-auto">
            <BioText content={current?.content} testId="about-current-bio" />
          </div>
        </div>

        {/* ── FUTURE APPROVED VERSION ──────────────────────────── */}
        <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(7,27,58,0.6)', border: '1px solid rgba(251,191,36,0.1)' }}
          data-testid="about-future-panel">
          <div className="px-6 py-5 border-b" style={{ borderColor: 'rgba(251,191,36,0.06)' }}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="flex items-center gap-2.5 mb-2">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-cream/40">Version 2</span>
                  <StatusBadge status="stored" />
                </div>
                <h2 className="text-cream/85 font-semibold text-base">Future Approved About</h2>
                <p className="text-[11px] text-amber-400/50 mt-1.5 leading-relaxed">
                  Introduces "Nigerian-British" and names Nigeria &amp; UK contexts.<br />
                  Do NOT publish until British citizenship ceremony is complete.
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowPreview('future')}
                data-testid="about-preview-future"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] transition-all"
                style={{ color: 'rgba(212,204,188,0.45)', border: '1px solid rgba(212,204,188,0.08)' }}
              >
                <Eye size={11} /> Preview
              </button>
              <button
                onClick={() => setEditTarget('future')}
                data-testid="about-edit-future"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] transition-all"
                style={{ color: 'rgba(201,147,22,0.65)', border: '1px solid rgba(201,147,22,0.15)' }}
              >
                <Pencil size={11} /> Edit
              </button>
              <button
                onClick={() => setShowCompare(true)}
                data-testid="about-compare-inline-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] transition-all"
                style={{ color: 'rgba(212,204,188,0.45)', border: '1px solid rgba(212,204,188,0.08)' }}
              >
                <GitCompare size={11} /> Compare
              </button>
              <button
                onClick={() => setShowPublishConfirm(true)}
                data-testid="about-publish-future-btn"
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[11px] font-semibold transition-all ml-auto"
                style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.25)' }}
              >
                <Upload size={11} /> Publish Future Version
              </button>
            </div>
          </div>
          <div className="p-6 max-h-80 overflow-y-auto">
            <BioText content={future?.content} testId="about-future-bio" />
          </div>
        </div>
      </div>

      {/* ── Version History ──────────────────────────────────────── */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(7,27,58,0.4)', border: '1px solid rgba(201,147,22,0.06)' }}>
        <button
          onClick={() => setShowHistory(v => !v)}
          data-testid="about-history-toggle"
          className="w-full flex items-center justify-between px-6 py-4 text-left transition-all hover:bg-cream/[0.02]"
        >
          <div className="flex items-center gap-2.5">
            <History size={14} className="text-cream/30" />
            <span className="text-sm font-medium text-cream/60">Version History</span>
            {history.length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                style={{ background: 'rgba(201,147,22,0.12)', color: '#C99316' }}>{history.length}</span>
            )}
          </div>
          {showHistory ? <ChevronUp size={14} className="text-cream/30" /> : <ChevronDown size={14} className="text-cream/30" />}
        </button>

        {showHistory && (
          <div className="border-t px-6 pb-6" style={{ borderColor: 'rgba(201,147,22,0.05)' }}>
            {history.length === 0 ? (
              <p className="text-xs text-cream/25 py-6 text-center">No history yet. History is recorded when the Future version is published or rolled back.</p>
            ) : (
              <div className="space-y-3 mt-4">
                {history.map(h => (
                  <div key={h.id} className="flex items-start justify-between gap-4 p-4 rounded-xl"
                    style={{ background: 'rgba(7,15,32,0.4)', border: '1px solid rgba(201,147,22,0.06)' }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-cream/60 truncate">{h.version_label}</p>
                      <p className="text-[11px] text-cream/30 mt-0.5">
                        Archived {fmtDt(h.replaced_at)} · by {h.replaced_by}
                      </p>
                      <p className="text-[11px] text-cream/20 mt-1 line-clamp-2 font-serif leading-relaxed">
                        {(h.content || '').substring(0, 120)}…
                      </p>
                    </div>
                    <button
                      onClick={() => handleRollback(h.id)}
                      disabled={rollingBack === h.id}
                      data-testid={"about-rollback-" + h.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium flex-shrink-0 transition-all disabled:opacity-40"
                      style={{ color: 'rgba(212,204,188,0.4)', border: '1px solid rgba(212,204,188,0.08)' }}
                    >
                      {rollingBack === h.id ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} />}
                      Rollback
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Link to public About page ─────────────────────────── */}
      <div className="flex items-center justify-end">
        <a
          href="/about"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-cream/30 hover:text-gold transition-colors"
        >
          View public About page <ArrowRight size={11} />
        </a>
      </div>

      {/* ── Modals ──────────────────────────────────────────────── */}
      {editTarget && (
        <EditModal
          versionType={editTarget}
          content={editTarget === 'current' ? current?.content : future?.content}
          onSave={handleSaveEdit}
          onClose={() => setEditTarget(null)}
        />
      )}
      {showPreview && (
        <PreviewModal
          content={showPreview === 'current' ? current?.content : future?.content}
          label={showPreview === 'current' ? 'Current Live About' : 'Future Approved About'}
          onClose={() => setShowPreview(null)}
        />
      )}
      {showCompare && (
        <CompareView
          current={current?.content}
          future={future?.content}
          onClose={() => setShowCompare(false)}
        />
      )}
      {showPublishConfirm && (
        <PublishConfirmDialog
          onConfirm={handlePublish}
          onCancel={() => setShowPublishConfirm(false)}
          publishing={publishing}
        />
      )}
    </div>
  );
};

export default AdminAbout;
